import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Fragment, useState } from "react";
import { useFinance } from "./FinanceContext";
import { metrics, sum } from "./data/model";
import { MONTHS, SEED } from "./data/seedData";
import type { BudgetItem } from "./data/types";
import { AnnualBar } from "./components/AnnualBar";
import { MonthInline } from "./components/MonthPicker";
import { StatusBadge } from "./components/StatusChip";
import { toneColor } from "./components/tone";

export function BudgetVsActualPage() {
  const { model, fmt, scf, selMonth, openVouchers } = useFinance();
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const query = q.trim().toLowerCase();
  const match = (it: BudgetItem) =>
    !query || it.name.toLowerCase().includes(query) || it.led.toLowerCase().includes(query);
  function toggle(l: string) {
    const n = new Set(collapsed);
    if (n.has(l)) n.delete(l);
    else n.add(l);
    setCollapsed(n);
  }

  let T = { annual: 0, ytdB: 0, ytdA: 0, cmB: 0, cmA: 0, fc: 0 };
  model.items.forEach((it) => {
    const m = metrics(it, selMonth);
    T = {
      annual: T.annual + sum(it.planned) * scf,
      ytdB: T.ytdB + m.ytdB * scf,
      ytdA: T.ytdA + m.ytdA,
      cmB: T.cmB + m.cmB * scf,
      cmA: T.cmA + m.cmA,
      fc: T.fc + m.forecast,
    };
  });

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Budget vs Actual Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Read-only analysis · click any Actual to drill into Tally vouchers
      </Typography>
      <AnnualBar />
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search cost head…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <MonthInline />
        <Button size="small" onClick={() => setCollapsed(new Set())}>
          Expand all
        </Button>
        <Button
          size="small"
          onClick={() => setCollapsed(new Set(SEED.categories.map((c) => c.letter)))}
        >
          Collapse all
        </Button>
      </Stack>
      <TableContainer sx={{ maxHeight: "70vh" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Cost Head</TableCell>
              <TableCell align="right">Annual Budget</TableCell>
              <TableCell align="right">YTD Budget</TableCell>
              <TableCell align="right">YTD Actual</TableCell>
              <TableCell align="right">Variance</TableCell>
              <TableCell align="right">Var %</TableCell>
              <TableCell align="right">{MONTHS[selMonth]} Budget</TableCell>
              <TableCell align="right">{MONTHS[selMonth]} Actual</TableCell>
              <TableCell align="right">Mth Var</TableCell>
              <TableCell align="right">Forecast</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SEED.categories.map((cat) => {
              const its = model.items.filter((i) => i.catLetter === cat.letter && match(i));
              if (query && !its.length) return null;
              const isCol = collapsed.has(cat.letter) && !query;
              let c = { annual: 0, ytdB: 0, ytdA: 0, cmB: 0, cmA: 0, fc: 0 };
              its.forEach((it) => {
                const m = metrics(it, selMonth);
                c = {
                  annual: c.annual + sum(it.planned) * scf,
                  ytdB: c.ytdB + m.ytdB * scf,
                  ytdA: c.ytdA + m.ytdA,
                  cmB: c.cmB + m.cmB * scf,
                  cmA: c.cmA + m.cmA,
                  fc: c.fc + m.forecast,
                };
              });
              const cv = c.ytdB - c.ytdA;
              const cst = !c.ytdB
                ? "g"
                : c.ytdA / c.ytdB > 1
                  ? "r"
                  : c.ytdA / c.ytdB > 0.9
                    ? "a"
                    : "g";
              return (
                <Fragment key={cat.letter}>
                  <TableRow
                    hover
                    onClick={() => toggle(cat.letter)}
                    sx={{ cursor: "pointer", bgcolor: "action.hover" }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>{cat.code}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {isCol ? "▸" : "▾"} {cat.name}{" "}
                      <Chip size="small" label={cat.type} sx={{ ml: 1 }} />
                    </TableCell>
                    <TableCell align="right">{fmt(c.annual)}</TableCell>
                    <TableCell align="right">{fmt(c.ytdB)}</TableCell>
                    <TableCell align="right">{fmt(c.ytdA)}</TableCell>
                    <TableCell align="right" sx={{ color: toneColor(cv >= 0 ? "pos" : "neg") }}>
                      {fmt(cv)}
                    </TableCell>
                    <TableCell align="right">
                      {c.ytdB ? ((cv / c.ytdB) * 100).toFixed(1) + "%" : "—"}
                    </TableCell>
                    <TableCell align="right">{fmt(c.cmB)}</TableCell>
                    <TableCell align="right">{fmt(c.cmA)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: toneColor(c.cmB - c.cmA >= 0 ? "pos" : "neg") }}
                    >
                      {fmt(c.cmB - c.cmA)}
                    </TableCell>
                    <TableCell align="right">{fmt(c.fc)}</TableCell>
                    <TableCell align="right">
                      <StatusBadge status={cst} />
                    </TableCell>
                  </TableRow>
                  {!isCol
                    ? its.map((it) => {
                        const m = metrics(it, selMonth);
                        return (
                          <TableRow key={it.id}>
                            <TableCell>{it.code}</TableCell>
                            <TableCell>{it.name}</TableCell>
                            <TableCell align="right">{fmt(m.annual * scf)}</TableCell>
                            <TableCell align="right">{fmt(m.ytdB * scf)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                cursor: "pointer",
                                color: "primary.main",
                                textDecoration: "underline dotted",
                              }}
                              onClick={() => openVouchers(it, "ytd")}
                            >
                              {fmt(m.ytdA)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: toneColor(m.ytdB * scf - m.ytdA >= 0 ? "pos" : "neg") }}
                            >
                              {fmt(m.ytdB * scf - m.ytdA)}
                            </TableCell>
                            <TableCell align="right">
                              {m.ytdB
                                ? (((m.ytdB * scf - m.ytdA) / (m.ytdB * scf)) * 100).toFixed(1) +
                                  "%"
                                : "—"}
                            </TableCell>
                            <TableCell align="right">{fmt(m.cmB * scf)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                cursor: "pointer",
                                color: "primary.main",
                                textDecoration: "underline dotted",
                              }}
                              onClick={() => openVouchers(it, "cm")}
                            >
                              {fmt(m.cmA)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: toneColor(m.cmB * scf - m.cmA >= 0 ? "pos" : "neg") }}
                            >
                              {fmt(m.cmB * scf - m.cmA)}
                            </TableCell>
                            <TableCell align="right">{fmt(m.forecast)}</TableCell>
                            <TableCell align="right">
                              <StatusBadge status={m.status} />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : null}
                </Fragment>
              );
            })}
            <TableRow sx={{ "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }}>
              <TableCell colSpan={2}>GRAND TOTAL</TableCell>
              <TableCell align="right">{fmt(T.annual)}</TableCell>
              <TableCell align="right">{fmt(T.ytdB)}</TableCell>
              <TableCell align="right">{fmt(T.ytdA)}</TableCell>
              <TableCell
                align="right"
                sx={{ color: toneColor(T.ytdB - T.ytdA >= 0 ? "pos" : "neg") }}
              >
                {fmt(T.ytdB - T.ytdA)}
              </TableCell>
              <TableCell align="right">
                {T.ytdB ? (((T.ytdB - T.ytdA) / T.ytdB) * 100).toFixed(1) + "%" : "—"}
              </TableCell>
              <TableCell align="right">{fmt(T.cmB)}</TableCell>
              <TableCell align="right">{fmt(T.cmA)}</TableCell>
              <TableCell
                align="right"
                sx={{ color: toneColor(T.cmB - T.cmA >= 0 ? "pos" : "neg") }}
              >
                {fmt(T.cmB - T.cmA)}
              </TableCell>
              <TableCell align="right">{fmt(T.fc)}</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
